import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { familiesApi } from './api';
import { keys } from '@/lib/query/keys';
import { useListState } from '@/lib/hooks/useListState';
import { useDebounced } from '@/lib/hooks/useDebounced';
import { useLocale } from '@/lib/i18n/useLocale';
import { useGate } from '@/lib/rbac/Gate';
import { resolveI18n } from '@/lib/i18n/content';
import { DataView } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Pagination } from '@/lib/ui/Pagination';
import { PageHeader, MonoValue } from '@/lib/ui/Typography';
import { ContentStatusPill } from '@/lib/ui/StatusPill';
import { Tag } from '@/lib/ui/Tag';
import { PermissionButton } from '@/lib/ui/Button';
import { Select } from '@/lib/ui/Select';
import { TextInput } from '@/lib/ui/Field';
import { formatRelative } from '@/lib/format';
import type { ProductFamily } from '@/lib/api/types';

/** Product Families list (spec §9.2) — a bespoke typed collection with its own columns. */
export function FamiliesListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { locale } = useLocale();

  const STATUS_OPTIONS = [
    { value: '', label: t('families.statusAll') },
    { value: 'draft', label: t('families.statusDraft') },
    { value: 'in_review', label: t('families.statusInReview') },
    { value: 'scheduled', label: t('families.statusScheduled') },
    { value: 'published', label: t('families.statusPublished') },
    { value: 'archived', label: t('families.statusArchived') },
  ];

  const CATALOG_LABEL: Record<string, string> = {
    preview: t('families.catalogPreview'),
    ready: t('families.catalogReady'),
  };

  const list = useListState({ status: '' });
  const [rawSearch, setRawSearch] = useState('');
  const debounced = useDebounced(rawSearch, 300);

  const { allowed: canWrite } = useGate('content:write');

  const query = useQuery({
    queryKey: keys.families.list({ ...list.params, q: debounced }),
    queryFn: () => familiesApi.list({ ...list.params, q: debounced || undefined }),
    placeholderData: (prev) => prev,
  });

  const columns: Column<ProductFamily>[] = [
    {
      id: 'name',
      header: t('families.colName'),
      cell: (r) => <span className="font-medium text-ink-deep">{resolveI18n(r.name, locale)}</span>,
    },
    { id: 'status', header: t('common.status'), cell: (r) => <ContentStatusPill status={r.status} /> },
    {
      id: 'slug',
      header: t('common.slug'),
      cell: (r) => <MonoValue className="text-ink-mid">{r.slug}</MonoValue>,
    },
    {
      id: 'catalog',
      header: t('families.colCatalog'),
      cell: (r) => (
        <Tag variant={r.catalog_status === 'ready' ? 'teal' : 'neutral'}>
          {CATALOG_LABEL[r.catalog_status] ?? r.catalog_status}
        </Tag>
      ),
    },
    {
      id: 'sort',
      header: t('families.colOrder'),
      align: 'end',
      cell: (r) => <MonoValue>{r.sort_order}</MonoValue>,
    },
    {
      id: 'updated',
      header: t('common.updated'),
      align: 'end',
      cell: (r) => <span className="text-ink-mute">{formatRelative(r.updated_at)}</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t('families.eyebrow')}
        title={t('families.title')}
        count={query.data?.meta?.pagination.total}
        actions={
          <PermissionButton
            allowed={canWrite}
            reason={t('families.createPermission')}
            onClick={() => navigate('/families/new')}
          >
            {t('families.newFamily')}
          </PermissionButton>
        }
      />

      <div className="mb-md flex flex-wrap items-end gap-sm">
        <div className="w-[180px]">
          <Select
            label={t('common.status')}
            value={(list.filters.status as string) || ''}
            onValueChange={(v) => list.setFilter('status', v)}
            options={STATUS_OPTIONS}
          />
        </div>
        <div className="min-w-[180px] flex-1">
          <TextInput label={t('common.search')} value={rawSearch} onChange={(e) => setRawSearch(e.target.value)} />
        </div>
      </div>

      <DataView query={query} isEmpty={(d) => d.data.length === 0}>
        {(d) => (
          <>
            <DataTable
              columns={columns}
              rows={d.data}
              rowKey={(r) => r.id}
              onRowClick={(r) => navigate(`/families/${r.id}`)}
            />
            {d.meta?.pagination && (
              <Pagination meta={d.meta.pagination} onPageChange={list.setPage} onPerPageChange={list.setPerPage} />
            )}
          </>
        )}
      </DataView>
    </div>
  );
}
